import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/auth';
import { useProfileStore } from '../stores/profileStore';
import { useMFAStore } from '../stores/mfaStore';
import { useMFARequired } from '../hooks/useMFARequired';
import { LoadingSpinner } from './LoadingSpinner';
import { AuthMFA } from './auth/AuthMFA';
import { DashboardNavigation } from './navigation/DashboardNavigation';
import { AccountInactive } from './common/AccountInactive';

interface PrivateRouteProps {
  children: React.ReactNode;
  activeKey?: string;
  breadCrumbs?: { key: string; route?: string }[];
  showMobileBreadCrumbs?: boolean;
}

export function PrivateRoute({
  children,
  activeKey,
  breadCrumbs,
  showMobileBreadCrumbs = false,
}: PrivateRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: loadingProfile } = useProfileStore();
  const { requireMFA, checking: checkingMFA } = useMFARequired();
  const { markMfaVerified } = useMFAStore();
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });
  }, [pathname]);

  if (authLoading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: `${pathname}${search}` }} />;
  }

  if (checkingMFA || (loadingProfile && !profile)) {
    return <LoadingSpinner />;
  }

  if (!profile) {
    return <Navigate to="/signup" replace />;
  }

  if (!profile.last_name) {
    return <Navigate to="/signup" replace />;
  }

  if (profile.status !== 'active') {
    return <AccountInactive status={profile.status} />;
  }

  if (requireMFA) {
    return <AuthMFA onVerified={() => markMfaVerified()} />;
  }

  // if admin redirect to admin space
  if (profile.role === 'administrator') {
    return <Navigate to="/admin" replace />;
  }

  return (
    <>
      <DashboardNavigation
        activeKey={activeKey}
        breadCrumbs={breadCrumbs}
        showMobileBreadCrumbs={showMobileBreadCrumbs}
      />
      {children}
    </>
  );
}
